import * as React from "react";
import { usePublicClient } from "wagmi";
import { FallbackProvider, JsonRpcProvider } from "ethers";

/**
 * Convert a Viem public client to an ethers.js provider
 * @param {Object} publicClient - The Viem public client from Wagmi
 * @returns {JsonRpcProvider|FallbackProvider|undefined} The ethers.js provider
 */
export function publicClientToProvider(publicClient) {
  if (!publicClient) return undefined;

  const { chain, transport } = publicClient;

  // Define the network configuration for ethers.js
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };

  // Handle fallback transport (multiple RPC endpoints)
  if (transport.type === "fallback") {
    const providers = transport.transports.map(
      ({ value }) => new JsonRpcProvider(value?.url, network)
    );

    if (providers.length === 1) return providers[0];
    return new FallbackProvider(providers);
  }

  // Handle single HTTP transport
  return new JsonRpcProvider(transport.url, network);
}

/** 
 * Hook to get an ethers.js provider from Wagmi's public client
 * Converts the Viem public client to an ethers.js provider for backward compatibility
 * 
 * @param {Object} options - Options object
 * @param {number} options.chainId - Optional chain ID to get provider for specific chain
 * @returns {JsonRpcProvider|FallbackProvider|undefined} The ethers.js provider
 */
export function useEthersProvider({ chainId } = {}) {
  const publicClient = usePublicClient({ chainId });

  return React.useMemo(
    () => publicClientToProvider(publicClient),
    [publicClient]
  );
}